import {
  Component,
  HostListener,
  OnInit,
  forwardRef,
  HostBinding,
  input,
  viewChild,
  computed,
  OnDestroy,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import moment from "moment";
import { counterMinBookingDate } from '@otas/shared/booking-day-policy';
import {
  BsDatepickerDirective,
  BsDatepickerConfig,
  DatepickerDateCustomClasses,
} from "ngx-bootstrap/datepicker";

const CUSTOM_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DateTime),
  multi: true,
};

@Component({
  selector: "datetime",
  providers: [CUSTOM_ACCESSOR],
  standalone: false,
  template: `
    @if (isDatepickerMode()) {
    <div
      [class.form-group]="true"
      [class.input-group]="!datepickerOptions().hideIcon"
      [class.date]="true"
      style="width: fit-content !important;"
    >
      <input
        type="text"
        class="form-control form-control-sm"
        [disabled]="readonly()"
        [attr.required]="required"
        [attr.placeholder]="datepickerOptions().placeholder || 'Choose date'"
        [attr.tabindex]="tabindex"
        #datepickerRef="bsDatepicker"
        bsDatepicker
        [triggers]="dateModel == null || !datepickerOptions().keyboardNavigation ? 'click' : ''"
        [bsConfig]="bsDatepickerConfig"
        [(ngModel)]="dateModel"
        (ngModelChange)="onDateChange($event)"
        [dateCustomClasses]="dateCustomClasses"
        (keyup)="checkEmptyValue($event)"
        (blur)="onTouched()"
        dateInputKeyboardHandler
        [dateFormat]="datepickerOptions().format || 'DD-MMM-YYYY'"
        (dateChanged)="onDateChangedFromKeyboard($event)"
        [keyboardNavigationEnable]="datepickerOptions().keyboardNavigation"
        (onHidden)="onHidden($event)"
      />
      <span
        *ngIf="!datepickerOptions().hideIcon"
        (click)="showDatepicker()"
        class="input-group-addon date-icon"
      >
        <span
          [ngClass]="datepickerOptions().icon || 'glyphicon glyphicon-th'"
        ></span>
      </span>
    </div>
    } @else {
    <ng-template #timePickerBlock>
      <timepicker [(ngModel)]="time" (ngModelChange)="onTimeChange($event)">
      </timepicker>
    </ng-template>
    <input
      [ngModel]="time | date : 'hh:mm aa'"
      [popover]="timePickerBlock"
      [outsideClick]="true"
      placement="bottom"
    />
    }
  `,
  styles: [
    ".date-icon { cursor: pointer; z-index: 100; }",
    ".ng2-datetime *[hidden] { display: none; }",
  ],
})
export class DateTime implements OnInit, OnDestroy, ControlValueAccessor {
  bsDatepickerConfig: Partial<BsDatepickerConfig>;
  dateModel: Date;
  time: Date = new Date();
  date: Date = new Date();
  dateCustomClasses: DatepickerDateCustomClasses[] = [
    { date: this.date, classes: ["bg-info"] },
  ];
  disabledDates: Date[] = [];
  timepickerOptions = input<any>(false, { alias: "timepicker" });
  datepickerOptions = input<any>({}, { alias: "datepicker" });
  readonly = input<boolean>(false);
  required = input<boolean>();
  tabindex = input<boolean>();
  datepickerRef = viewChild<BsDatepickerDirective>("datepickerRef");
  private onChange: (_: any) => void = () => { };
  onTouched: () => void = () => { };
  readonly isDatepickerMode = computed(() => this.datepickerOptions() !== false);
  readonly isTimepickerMode = computed(() => this.timepickerOptions() !== false || this.datepickerOptions().timeWisePoojaReport);
  dateFormat = computed(() => this.isDatepickerMode() && this.isTimepickerMode()
    ? this.datepickerOptions().format?.toUpperCase()
    + ', h:mm a'
    : this.datepickerOptions().format.toUpperCase() || 'DD-MMM-YYYY');
  ngOnInit(): void {
    this.initDatetimePicker();
  }

  /** Caller's floor adjusted by the booking-day policy (never raised). */
  private policyMinDate(startDate: Date | undefined): Date | undefined {
    const min = counterMinBookingDate();
    if (min === null) { return undefined; }
    return startDate && min < startDate ? min : startDate;
  }

  private initDatetimePicker(): void {
    if (this.datepickerOptions()) {
      this.bsDatepickerConfig = {
        dateInputFormat: this.dateFormat(),
        containerClass: this.datepickerOptions().theme || "theme-default",
        adaptivePosition: true,
        daysDisabled: this.datepickerOptions().daysDisabled,
        // Booking-day policy (temple.ONLINE_DAY_END): a D-N past window lowers
        // the caller's floor, PAST removes it. Replaces the retired
        // OLD_POOJA_DATE_BILLING (isOldPoojaDateSuggession) blanket unlock.
        minDate: this.policyMinDate(this.datepickerOptions().startDate),
        maxDate: this.datepickerOptions().endDate,
        datesDisabled: this.disabledDates,
        minMode: this.datepickerOptions().minMode,
        showClearButton: this.datepickerOptions().hasClearButton,
        withTimepicker: this.datepickerOptions().timeWisePoojaReport || false,
        keepDatepickerOpened: this.datepickerOptions().keepDatepickerOpened || true,
        showWeekNumbers: this.datepickerOptions().showWeekNumbers,
      };

      if (this.datepickerOptions().endDate) {
        const endDate = this.datepickerOptions().endDate;
        const currentDate = new Date();
        if (
          endDate.getFullYear() === currentDate.getFullYear() &&
          endDate.getMonth() === currentDate.getMonth() &&
          endDate.getDate() === currentDate.getDate()
        ) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(0, 0, 0, 0);
          this.datepickerOptions().endDate = tomorrow;
          this.disabledDates = [tomorrow];
        }
      }
      this.updateModel(this.date);
    }
  }

  onDateChange(value: Date): void {
    this.date = value;
    this.dateModel = value;
    if (!value) {
      this.onChange(null);
    }
  }

  onTimeChange(event: Date): void {
    this.date.setHours(
      event.getHours(),
      event.getMinutes(),
      event.getSeconds()
    );
    this.emitValue();
  }

  private updateModel(date: Date): void {
    this.dateModel = date;
    this.time = new Date(date);
  }

  writeValue(value: any): void {
    if (value) {
      if (typeof value === "string") {
        const format =
          this.datepickerOptions() !== false
            ? this.datepickerOptions().format || "MM/DD/YYYY"
            : "MM/DD/YYYY";
        const timeFormat =
          this.timepickerOptions() !== false
            ? this.timepickerOptions().showMeridian
              ? " hh:mm A"
              : " HH:mm"
            : "";
        const combinedFormat = timeFormat ? `${format}${timeFormat}` : format;
        const momentDate = moment(value, combinedFormat);
        this.date = momentDate.isValid() ? momentDate.toDate() : new Date();
      } else if (value instanceof Date) {
        this.date = new Date(value);
      }
      this.updateModel(this.date);
    } else {
      if (
        this.timepickerOptions() !== false &&
        this.datepickerOptions() === false &&
        !this.date
      ) {
        this.date = new Date();
        this.time = new Date();
        this.updateModel(this.date);
      } else {
        this.date = null;
        this.updateModel(null);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void { }

  @HostListener("blur")
  handleBlur(): void {
    this.onTouched();
  }

  @HostBinding("attr.tabindex")
  get tabindexAttr(): string | undefined {
    return this.tabindex === undefined ? "-1" : undefined;
  }

  showDatepicker(): void {
    this.datepickerRef()?.show();
  }

  clearModels(): void {
    this.date = null;
    this.time = null;
    this.updateModel(null);
    this.onChange(null);
  }

  checkEmptyValue(e: KeyboardEvent): void {
    if ((e.target as HTMLInputElement).value === "") {
      this.checkBothPickersEmpty();
    }
  }

  private checkBothPickersEmpty(): void {
    if (
      this.timepickerOptions() === false ||
      this.datepickerOptions() === false ||
      !this.dateModel
    ) {
      this.onChange(null);
    }
  }

  private emitValue(): void {
    if (!this.date) {
      this.onChange(null);
      return;
    }
    if (this.datepickerOptions() !== false) {
      this.onChange(this.date);
    } else if (this.timepickerOptions() !== false) {
      const timeFormat = this.timepickerOptions().showMeridian
        ? "hh:mm A"
        : "HH:mm";
      this.onChange(moment(this.date).format(timeFormat));
    }
  }
  onDateChangedFromKeyboard(newDate: Date): void {
    this.date = newDate;
    this.dateModel = newDate;
    this.emitValue();
  }
  onHidden(event: any) {
    this.emitValue();
  }
  ngOnDestroy(): void {
    this.date = null;
    this.time = null;
    this.dateModel = null;

    this.dateCustomClasses = [];
    this.disabledDates = [];
    this.bsDatepickerConfig = null;

    this.timepickerOptions = null;
    this.datepickerOptions = null;

    this.onChange = () => { };
    this.onTouched = () => { };

    this.datepickerRef = null;
  }

}
